import { Coding, Location, Practitioner } from '@medplum/fhirtypes';
import { AMBULATORY_ENCOUNTER_CLASS_CODING, VIRTUAL_ENCOUNTER_CLASS_CODING } from '@bariendo/lib/codings';
import { PractitionerInput } from '@/components/PractitionerInput';
import { VisitTypeInput } from '@/pages/SchedulingPage/components/VisitTypeInput';
import { LocationInput } from '@/pages/SchedulingPage/components/LocationInput';
import { TimezoneInput } from '@/pages/SchedulingPage/components/TimezoneInput';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLocationTimezone } from '@/pages/SchedulingPage/hooks/useLocationTimezone';

interface SharedAvailabilityFieldsProps {
  practitioner: Practitioner | null;
  encounterClass: Coding;
  location: Location | null;
  timezone: string;
  onPractitionerChange: (resource: Practitioner | null) => void;
  onVisitTypeChange: (encounterClass: Coding) => void;
  onLocationChange: (resource: Location | null) => void;
  onTimezoneChange: (value: string) => void;
  practitioners?: Practitioner[];
}

export function SharedAvailabilityFields({
  practitioner,
  encounterClass,
  location,
  timezone,
  onPractitionerChange,
  onVisitTypeChange,
  onLocationChange,
  onTimezoneChange,
  practitioners,
}: SharedAvailabilityFieldsProps) {
  const isInPerson = encounterClass.code === AMBULATORY_ENCOUNTER_CLASS_CODING.code;
  const { timezoneSuffix, resolveLocationChange, resolveVisitTypeChange, resolveTimezoneSelection } =
    useLocationTimezone();

  const {
    currentUserPermissions: { canManageSchedules },
  } = useCurrentUser();

  const handleVisitTypeChange = (newEncounterClass: Coding) => {
    const newIsVirtual = newEncounterClass.code === VIRTUAL_ENCOUNTER_CLASS_CODING.code;
    const { timezone: resolvedTimezone } = resolveVisitTypeChange(newIsVirtual, location, timezone);

    if (newIsVirtual) {
      onLocationChange(null);
    }

    onTimezoneChange(resolvedTimezone);
    onVisitTypeChange(newEncounterClass);
  };

  const handleLocationChange = (newLocation: Location | null) => {
    const { locationTimezone } = resolveLocationChange(newLocation);

    if (isInPerson) {
      onTimezoneChange(locationTimezone || '');
    } else if (locationTimezone) {
      onTimezoneChange(locationTimezone);
    }

    onLocationChange(newLocation);
  };

  const handleTimezoneChange = (value: string) => {
    resolveTimezoneSelection(value);
    onTimezoneChange(value);
  };

  return (
    <>
      <PractitionerInput
        value={practitioner}
        onChange={onPractitionerChange}
        practitioners={practitioners}
        disabled={!canManageSchedules}
      />

      <VisitTypeInput value={encounterClass} onChange={handleVisitTypeChange} />

      {isInPerson && <LocationInput value={location} onChange={handleLocationChange} />}

      <TimezoneInput
        value={timezone}
        onChange={handleTimezoneChange}
        displaySuffix={timezoneSuffix}
        disabled={isInPerson}
      />
    </>
  );
}
